<template>

  <div
      class="w-3/4 m-auto flex flex-col items-center justify-center"
      style="background-color: white; height: 500px; border-radius: 15px;"
  >
    <button
        class="form-action-btn btn btn-success mb-4"
        style="width: 250px"
        @click="toggleVoiceRecognition"
    >
      {{ buttonText }}
    </button>

    <div style="width: fit-content">
      <audio ref="audioPlayer" id="audioPlayer"></audio>
      <div style="width: fit-content; height: 260px">
        <template v-if="isListening">
          <img
              :class="{ 'hidden': !isListening }"
              id="wave_img"
              ref="waveImg"
              width="200"
              height="180"
              src="@/assets/images/sound-wave.jpg"
          />
        </template>
      </div>
    </div>
  </div>

</template>

<script setup>
import {ref, onBeforeUnmount, onMounted} from "vue";
// import DefaultLayout from "./layouts/DefaultLayout.vue";

const audioPlayer = ref(null);
const waveImg = ref(null);

const recognition = ref(null);
const watchdogTimer = ref(null);
const ws = ref(null);
const audioBlob = ref([]);
const silentAudioStream = ref(null);
const gainNode = ref(null);
const POKE_INTERVAL_MS = 5000;
const audioContext = ref(null);
const pokeInterval = ref(null);
const isPlayingAnswer = ref(false);
const alreadyStarted = ref(false);
const silenceTimeout = 20000;
const hasReceivedInit = ref(false);
const sessionId = ref(null);
const isListening = ref(false);
const buttonText = ref("Start");
let companyTableName = "";

onMounted(() => {
  companyTableName = localStorage.getItem("tableName");
});

// Lifecycle cleanup
onBeforeUnmount(() => {
  stopRecognition();
  if (ws.value) ws.value.close();
});

const stopRecognition = () => {
  if (recognition.value) {
    recognition.value.stop();
  }
  if (silentAudioStream.value) {
    silentAudioStream.value.getTracks().forEach((track) => track.stop());
    silentAudioStream.value = null;
  }
  if (audioContext.value) {
    audioContext.value.close();
    audioContext.value = null;
  }
  if (pokeInterval.value) {
    clearInterval(pokeInterval.value);
    pokeInterval.value = null;
  }
};

const connectWebSocket = () => {
  return new Promise((resolve, reject) => {
    ws.value = new WebSocket(process.env.VUE_APP_SOCKET_HOST);

    ws.value.onopen = () => {
      resolve(ws.value);
    };

    ws.value.onerror = (error) => {
      console.error("WebSocket error:", error);
      reject(error);
    };

    ws.value.onmessage = async (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === "session_init") {
          sessionId.value = message.sessionId;
        }
        switch (message.type) {
          case "audio":
            audioBlob.value.push(new Uint8Array(message.data));
            break;

          case "end":
            if (audioBlob.value.length > 0) {
              if (
                  isPlayingAnswer.value &&
                  audioPlayer.value &&
                  !audioPlayer.value.paused
              ) {
                audioBlob.value = [];
                return;
              }
              playAudioBlob();
            }
            break;

          case "error":
            console.error("Error:", message.message);
            break;
        }
      } catch (error) {
        console.error("Error processing WebSocket message:", error);
      }
    };

    ws.value.onclose = () => {
    };
  });
};

const playAudioBlob = () => {
  clearTimeout(watchdogTimer.value);
  const fullLength = audioBlob.value.reduce(
      (acc, chunk) => acc + chunk.length,
      0
  );
  const fullAudio = new Uint8Array(fullLength);
  let offset = 0;
  for (const chunk of audioBlob.value) {
    fullAudio.set(chunk, offset);
    offset += chunk.length;
  }

  const blob = new Blob([fullAudio], {type: "audio/mpeg"});
  const audioUrl = URL.createObjectURL(blob);

  if (audioPlayer.value) {
    audioPlayer.value.src = audioUrl;
  }
  if (waveImg.value) {
    waveImg.value.style.display = "block";
  }

  isPlayingAnswer.value = true;
  alreadyStarted.value = true;

  const handleAudioEnd = () => {
    if (waveImg.value) waveImg.value.style.display = "none";
    isPlayingAnswer.value = false;
    if (ws.value?.readyState === WebSocket.OPEN) {
      ws.value.send(JSON.stringify({type: "playback_finished"}));
    }
    if (isListening.value) {
      resetWatchdogTimer();
    }
    audioPlayer.value.removeEventListener("ended", handleAudioEnd);
  };

  audioPlayer.value.addEventListener("ended", handleAudioEnd);

  audioPlayer.value.play().catch((error) => {
    console.error("Audio playback failed:", error);
    isPlayingAnswer.value = false;
  });

  audioBlob.value = [];
};

const initializeAudio = async () => {
  try {
    audioContext.value = new (window.AudioContext ||
        window.webkitAudioContext)();
    const oscillator = audioContext.value.createOscillator();
    gainNode.value = audioContext.value.createGain();

    oscillator.frequency.value = 1;
    gainNode.value.gain.value = 0.001;

    oscillator.connect(gainNode.value);
    gainNode.value.connect(audioContext.value.destination);
    oscillator.start();

    silentAudioStream.value =
        audioContext.value.createMediaStreamDestination().stream;
    pokeInterval.value = setInterval(pokeAudioStream, POKE_INTERVAL_MS);
  } catch (error) {
    console.error("Error initializing audio:", error);
    throw error;
  }
};

const pokeAudioStream = () => {
  if (gainNode.value && audioContext.value && audioContext.value.currentTime) {
    const now = audioContext.value.currentTime;
    gainNode.value.gain.setValueAtTime(0.001, now);
    gainNode.value.gain.linearRampToValueAtTime(0.0011, now + 0.1);
    gainNode.value.gain.linearRampToValueAtTime(0.001, now + 0.2);
  }
};

const initializeSpeechRecognition = async () => {
  if (!recognition.value) {
    recognition.value = new (window.SpeechRecognition ||
        window.webkitSpeechRecognition)();
    recognition.value.continuous = true;
    recognition.value.interimResults = true;
    recognition.value.lang = "en-US";

    recognition.value.onend = () => {
      if (isListening.value) {
        setTimeout(() => {
          try {
            recognition.value.start();
          } catch (error) {
            console.error("Recognition restart error:", error);
          }
        }, 50);
      }
    };

    recognition.value.onstart = () => {
      buttonText.value = "Listening...";
      isListening.value = true;
      if (
          ws.value?.readyState === WebSocket.OPEN &&
          !alreadyStarted.value &&
          !hasReceivedInit.value
      ) {
        ws.value.send(JSON.stringify({type: "init"}));
        hasReceivedInit.value = true;
      }
      resetWatchdogTimer();
    };

    recognition.value.onspeechend = () => {
      pokeAudioStream();
      resetWatchdogTimer();
    };

    recognition.value.onsoundstart = recognition.value.onspeechstart = () => {
      if (isPlayingAnswer.value) {
        stopAudioPlayback();
      }
      resetWatchdogTimer();
    };

    recognition.value.onresult = (event) => {
      const results = event.results;
      for (let i = event.resultIndex; i < results.length; i++) {
        if (
            isPlayingAnswer.value &&
            audioPlayer.value.currentTime &&
            audioPlayer.value.currentTime < 3
        ) {
          stopAudioPlayback();
        } else if (isPlayingAnswer.value) {
          recognition.value.stop();
        }

        resetWatchdogTimer();

        const transcript = results[i][0].transcript;
        if (
            results[i].isFinal &&
            transcript.trim() !== "" &&
            ws.value?.readyState === WebSocket.OPEN
        ) {
          ws.value.send(
              JSON.stringify({
                type: "question",
                text: transcript,
                table: companyTableName,
              })
          );
        }
      }
      pokeAudioStream();
    };

    recognition.value.onerror = (event) => {
      if (event.error !== "no-speech") {
        buttonText.value = "Start";
        isListening.value = false;
      }
    };
  }
  return recognition.value;
};

const stopAudioPlayback = () => {
  if (audioPlayer.value) {
    audioPlayer.value.pause();
    audioPlayer.value.src = "";
  }
  if (waveImg.value) waveImg.value.style.display = "none";
  audioBlob.value = [];
};

const resetWatchdogTimer = () => {
  clearTimeout(watchdogTimer.value);
  watchdogTimer.value = setTimeout(() => {
    if (recognition.value) {
      try {
        recognition.value.stop();
        recognition.value.start();
      } catch (error) {
        console.error("Error restarting recognition:", error);
      }
    }
  }, silenceTimeout);
};

const toggleVoiceRecognition = async () => {
  if (buttonText.value === "Start") {
    try {
      await connectWebSocket();
      await initializeAudio();
      await initializeSpeechRecognition();
      recognition.value.start();
    } catch (error) {
      console.error("Error initializing:", error);
    }
  } else {
    stopRecognition();
    buttonText.value = "Start";
    isListening.value = false;
  }
};
</script>
